import { Play, RotateCcw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { QuoteCard } from '@/components/quotes/QuoteCard'
import type { QuoteCardData } from '@/components/quotes/quoteCardTypes'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

enum Phase {
  /** Application-sent card at rest. */
  SENT = 'sent',
  /** Beat 1 — the status line fades on its own, before anything moves. */
  FADING = 'fading',
  /** Beat 2 — an empty card grows to the priced height; only the header is showing. */
  EXPANDING = 'expanding',
  /** Beat 3 — body rows fade in, top to bottom. */
  REVEALING = 'revealing',
  DONE = 'done',
}

/** Cue times from the play click. Beat 2's 500ms growth lands before beat 3 starts. */
const CUES: { phase: Phase; at: number }[] = [
  { phase: Phase.FADING, at: 0 },
  { phase: Phase.EXPANDING, at: 260 },
  { phase: Phase.REVEALING, at: 800 },
  { phase: Phase.DONE, at: 1600 },
]

interface QuoteCardResolveDemoProps {
  sent: QuoteCardData
  resolved: QuoteCardData
  label: string
}

/**
 * Plays an application-sent card resolving into a priced one, so the handoff can be
 * judged as a sequence rather than a before/after pair.
 */
export function QuoteCardResolveDemo({ sent, resolved, label }: QuoteCardResolveDemoProps) {
  const [phase, setPhase] = useState(Phase.SENT)
  const [height, setHeight] = useState<number>()
  const sentRef = useRef<HTMLDivElement>(null)
  const pricedRef = useRef<HTMLDivElement>(null)
  const timers = useRef<number[]>([])

  // An explicit starting height, so the growth animates instead of snapping from auto.
  useEffect(() => {
    if (sentRef.current) setHeight(sentRef.current.offsetHeight)
  }, [])

  useEffect(() => () => timers.current.forEach(clearTimeout), [])

  function play() {
    timers.current.forEach(clearTimeout)
    timers.current = CUES.map(({ phase: next, at }) =>
      window.setTimeout(() => {
        // Opacity doesn't affect layout, so this reads the full height while rows are hidden.
        if (next === Phase.EXPANDING && pricedRef.current) setHeight(pricedRef.current.offsetHeight)
        setPhase(next)
      }, at)
    )
  }

  function reset() {
    timers.current.forEach(clearTimeout)
    setPhase(Phase.SENT)
    if (sentRef.current) setHeight(sentRef.current.offsetHeight)
  }

  const expanded = phase === Phase.EXPANDING || phase === Phase.REVEALING || phase === Phase.DONE
  const revealed = phase === Phase.REVEALING || phase === Phase.DONE

  return (
    <div className="relative">
      <div
        className="relative overflow-hidden transition-[height] duration-500 ease-out motion-reduce:transition-none"
        style={{ height }}
      >
        {/* Underneath from the start; its header aligns, so beat 2 grows one card, not a swap. */}
        <div
          ref={pricedRef}
          data-reveal={revealed ? 'on' : 'off'}
          aria-hidden={!expanded}
          inert={!expanded}
          className={cn(
            'resolve-demo-priced absolute inset-x-0 top-0 transition-opacity duration-300 motion-reduce:transition-none',
            expanded ? 'opacity-100' : 'opacity-0'
          )}
        >
          <QuoteCard quote={resolved} />
        </div>

        <div
          ref={sentRef}
          data-status={phase === Phase.SENT ? 'shown' : 'hidden'}
          aria-hidden={expanded}
          inert={expanded}
          className={cn(
            'resolve-demo-sent relative transition-opacity duration-300 motion-reduce:transition-none',
            expanded ? 'opacity-0' : 'opacity-100'
          )}
        >
          <QuoteCard quote={sent} />
        </div>
      </div>

      <div className="mt-2 flex items-center gap-2 lg:absolute lg:top-3 lg:left-full lg:mt-0 lg:ml-3">
        <span className="border-ink-200 text-ink-400 bg-ink-50 rounded-full border px-2 py-0.5 font-mono text-[10px] whitespace-nowrap">
          {label}
        </span>
        <Button
          size="icon-sm"
          variant="outline"
          aria-label={phase === Phase.SENT ? 'Play transition' : 'Replay transition'}
          onClick={phase === Phase.SENT ? play : reset}
        >
          {phase === Phase.SENT ? <Play /> : <RotateCcw />}
        </Button>
      </div>

      <style>
        {`
          /* Demo-only: staggers QuoteCard's real rows rather than add props to it. First
             child is the header, held visible so beat 2 is an empty card, not a blank one. */
          .resolve-demo-sent p {
            transition: opacity 250ms ease-out;
          }
          .resolve-demo-sent[data-status='hidden'] p {
            opacity: 0;
          }

          /* visibility, not just opacity — aria-hidden alone leaves rows tabbable. */
          .resolve-demo-priced > div > div > *:not(:first-child) {
            opacity: 0;
            visibility: hidden;
            transition: opacity 320ms ease-out, visibility 320ms;
          }
          .resolve-demo-priced[data-reveal='on'] > div > div > *:not(:first-child) {
            opacity: 1;
            visibility: visible;
          }
          .resolve-demo-priced[data-reveal='on'] > div > div > *:nth-child(2) { transition-delay: 0ms; }
          .resolve-demo-priced[data-reveal='on'] > div > div > *:nth-child(3) { transition-delay: 100ms; }
          .resolve-demo-priced[data-reveal='on'] > div > div > *:nth-child(4) { transition-delay: 200ms; }
          .resolve-demo-priced[data-reveal='on'] > div > div > *:nth-child(5) { transition-delay: 300ms; }
          .resolve-demo-priced[data-reveal='on'] > div > div > *:nth-child(6) { transition-delay: 400ms; }
          .resolve-demo-priced[data-reveal='on'] > div > div > *:nth-child(n + 7) { transition-delay: 500ms; }

          @media (prefers-reduced-motion: reduce) {
            .resolve-demo-sent p,
            .resolve-demo-priced > div > div > *:not(:first-child) {
              transition: none;
            }
          }
        `}
      </style>
    </div>
  )
}
